import React, { useEffect, useState } from 'react';
import Button from '@/components/buttons/Button';
import { useWorkflowStore } from '@/lib/workflows/store';
import { useUser } from '@/utils/useUser';
import { WorkflowCollaborator } from 'types';
import { Awareness } from 'y-protocols/awareness';
import { useYjsAwareness } from '@/lib/workflows/useYjsAwareness';
import Toggle from '@/components/atoms/Toggle';
import { toast } from 'sonner';
import nProgress from 'nprogress';

interface CollaboratorsModalProps {
  open: boolean;
  onClose: () => void;
  workflowId: string;
  isOwner: boolean;
  awareness?: Awareness | null;
}

const getDisplayName = (collaborator: WorkflowCollaborator) => {
  const first = collaborator.profile?.first_name;
  const last = collaborator.profile?.last_name;
  const name = [first, last].filter(Boolean).join(' ').trim();
  return name || collaborator.profile?.email || collaborator.user_id;
};

const getInitials = (name: string, email?: string | null) => {
  const source = (name || '').trim() || email || '';
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const Avatar = ({
  src,
  name,
  email,
  active,
}: {
  src?: string | null;
  name: string;
  email?: string | null;
  active?: boolean;
}) => (
  <div className='relative flex-shrink-0'>
    {src ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={src} alt='' className='h-9 w-9 rounded-full object-cover' />
    ) : (
      <div className='flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'>
        {getInitials(name, email)}
      </div>
    )}
    {active && (
      <span
        className='absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white dark:ring-gray-900'
        title='Active now'
      />
    )}
  </div>
);

export function CollaboratorsModal({
  open,
  onClose,
  workflowId,
  isOwner,
  awareness,
}: CollaboratorsModalProps) {
  const collaborators = useWorkflowStore((state) => state.collaborators);
  const activeCollaborators = useYjsAwareness(awareness);
  const setCollaborators = useWorkflowStore((state) => state.setCollaborators);
  const upsertCollaborator = useWorkflowStore(
    (state) => state.upsertCollaborator,
  );
  const removeCollaboratorLocal = useWorkflowStore(
    (state) => state.removeCollaboratorLocal,
  );
  const ownerProfile = useWorkflowStore((state) => state.workflowOwnerProfile);
  const setWorkflowOwnerProfile = useWorkflowStore(
    (state) => state.setWorkflowOwnerProfile,
  );
  const workflowOwnerId = useWorkflowStore((state) => state.workflowOwnerId);
  const shareWithTeam = useWorkflowStore((state) => state.shareWithTeam);
  const setShareWithTeam = useWorkflowStore((state) => state.setShareWithTeam);
  const { user, isTeamsPlan } = useUser();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'viewer' | 'editor'>('editor');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  // URL sharing state
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [shareLinkRole, setShareLinkRole] = useState<'viewer' | 'editor'>(
    'viewer',
  );
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!open || !workflowId) return;
    const fetchCollaborators = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `/api/workflows/${workflowId}/collaborators`,
        );
        if (response.ok) {
          const payload = await response.json();
          setCollaborators(payload.collaborators || []);
          setWorkflowOwnerProfile(payload.owner?.profile || null);
        }
      } catch (err) {
        console.error('Failed to load collaborators', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCollaborators();
  }, [open, workflowId, setCollaborators, setWorkflowOwnerProfile]);

  // Load current share link state (owner only)
  useEffect(() => {
    if (!open || !workflowId || !isOwner) return;
    const fetchShareLink = async () => {
      try {
        const res = await fetch(`/api/workflows/${workflowId}`);
        if (res.ok) {
          const wf = await res.json();
          setShareToken(wf.share_token || null);
          setShareLinkRole(
            wf.share_link_role === 'editor' ? 'editor' : 'viewer',
          );
        }
      } catch (err) {
        console.error('Failed to load share link', err);
      }
    };
    fetchShareLink();
  }, [open, workflowId, isOwner]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setRole('editor');
      setError(null);
      setSuccess(null);
      setRemoving(null);
      setLinkCopied(false);
    }
  }, [open]);

  if (!open) return null;

  const buildShareUrl = (token: string) => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/workflows/${workflowId}?share=${token}`;
  };

  const handleEnableShareLink = async () => {
    setShareLinkLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/workflows/${workflowId}/share-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: shareLinkRole }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to enable share link');
      }
      const data = await res.json();
      setShareToken(data.share_token);
      setShareLinkRole(data.share_link_role === 'editor' ? 'editor' : 'viewer');
      toast.success('Share link enabled');
    } catch (err: any) {
      toast.error(err.message || 'Failed to enable share link');
    } finally {
      setShareLinkLoading(false);
    }
  };

  const handleDisableShareLink = async () => {
    setShareLinkLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/workflows/${workflowId}/share-link`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to disable share link');
      }
      setShareToken(null);
      setLinkCopied(false);
      toast.success('Share link disabled');
    } catch (err: any) {
      toast.error(err.message || 'Failed to disable share link');
    } finally {
      setShareLinkLoading(false);
    }
  };

  const handleChangeShareLinkRole = async (newRole: 'viewer' | 'editor') => {
    if (newRole === shareLinkRole) return;
    const previousRole = shareLinkRole;
    setShareLinkRole(newRole); // optimistic
    if (!shareToken) return;
    try {
      const res = await fetch(`/api/workflows/${workflowId}/share-link`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to update share link role');
      }
      const data = await res.json();
      setShareLinkRole(data.share_link_role === 'editor' ? 'editor' : 'viewer');
    } catch (err: any) {
      // Roll back so the UI doesn't claim a role the server didn't accept.
      setShareLinkRole(previousRole);
      toast.error(err.message || 'Failed to update share link role');
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareToken) return;
    const url = buildShareUrl(shareToken);
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleToggleShareWithTeam = async () => {
    try {
      nProgress.start();
      const response = await fetch('/api/workflows/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workflowId,
          shareWithTeam: !shareWithTeam,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update sharing');
      }

      const data = await response.json();
      setShareWithTeam(!shareWithTeam);
      toast.success(data.message);
    } catch (err) {
      console.error('Failed to toggle share with team:', err);
      toast.error('Failed to update sharing settings');
    } finally {
      nProgress.done();
    }
  };

  const handleInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email) return;

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `/api/workflows/${workflowId}/collaborators`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, role }),
        },
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to add collaborator');
      }

      const collaborator = await response.json();
      upsertCollaborator(collaborator);
      setSuccess('Collaborator added');
      setEmail('');
      setRole('editor');
    } catch (err: any) {
      setError(err.message || 'Failed to invite collaborator');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (userId: string) => {
    setError(null);
    setSuccess(null);
    setRemoving(userId);
    try {
      const response = await fetch(
        `/api/workflows/${workflowId}/collaborators?userId=${userId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to remove collaborator');
      }

      removeCollaboratorLocal(userId);
      setSuccess('Collaborator removed');
    } catch (err: any) {
      setError(err.message || 'Failed to remove collaborator');
    } finally {
      setRemoving(null);
    }
  };

  const canRemove = (collaboratorId: string) => {
    if (!user) return false;
    if (isOwner) return collaboratorId !== workflowOwnerId;
    return collaboratorId === user.id;
  };

  const ownerName =
    ownerProfile?.first_name || ownerProfile?.last_name
      ? [ownerProfile?.first_name, ownerProfile?.last_name]
          .filter(Boolean)
          .join(' ')
          .trim()
      : ownerProfile?.email || workflowOwnerId || 'Owner';

  const activeUserIds = new Set(
    activeCollaborators.map((c) => c.userId).filter(Boolean),
  );

  const renderCollaboratorRow = (collaborator: WorkflowCollaborator) => {
    const isSelf = collaborator.user_id === user?.id;
    const name = getDisplayName(collaborator);
    const isActive = activeUserIds.has(collaborator.user_id);
    return (
      <li key={collaborator.id} className='flex items-center gap-3 py-2.5'>
        <Avatar
          src={collaborator.profile?.avatar_url}
          name={name}
          email={collaborator.profile?.email}
          active={isActive}
        />
        <div className='min-w-0 flex-1'>
          <div className='truncate text-sm font-medium text-gray-900 dark:text-white'>
            {name}
            {isSelf && (
              <span className='ml-1 text-gray-500 dark:text-gray-400'>
                (You)
              </span>
            )}
          </div>
          {collaborator.profile?.email && (
            <div className='truncate text-xs text-gray-500 dark:text-gray-400'>
              {collaborator.profile.email}
            </div>
          )}
        </div>
        <span className='text-xs text-gray-500 dark:text-gray-400 capitalize'>
          {collaborator.role}
        </span>
        {canRemove(collaborator.user_id) && (
          <button
            onClick={() => handleRemove(collaborator.user_id)}
            className='text-xs font-medium text-gray-500 hover:text-red-600 disabled:opacity-50 dark:text-gray-400 dark:hover:text-red-400'
            disabled={removing === collaborator.user_id}
            title='Remove'
          >
            {removing === collaborator.user_id ? '…' : 'Remove'}
          </button>
        )}
      </li>
    );
  };

  const ownerActive = workflowOwnerId
    ? activeUserIds.has(workflowOwnerId)
    : false;

  return (
    <div className='fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center z-50 p-4'>
      <div className='bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden'>
        {/* Header */}
        <div className='flex items-center justify-between px-6 pt-5 pb-4'>
          <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
            Share workflow
          </h3>
          <button
            onClick={onClose}
            aria-label='Close'
            className='-mr-2 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-200'
          >
            <svg
              className='h-4 w-4'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M6 6l12 12M6 18L18 6' />
            </svg>
          </button>
        </div>

        <div className='px-6 pb-6 space-y-5'>
          {error && (
            <div className='text-xs text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-lg dark:bg-red-950/30 dark:border-red-900/50 dark:text-red-300'>
              {error}
            </div>
          )}
          {success && (
            <div className='text-xs text-green-700 bg-green-50 border border-green-100 px-3 py-2 rounded-lg dark:bg-green-950/30 dark:border-green-900/50 dark:text-green-300'>
              {success}
            </div>
          )}

          {/* Invite by email — single row */}
          {isOwner && (
            <form onSubmit={handleInvite} className='space-y-2'>
              <div className='flex items-center gap-2'>
                <input
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder='Add people by email'
                  className='flex-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
                />
                <select
                  value={role}
                  onChange={(e) =>
                    setRole(e.target.value as 'viewer' | 'editor')
                  }
                  className='rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 pl-3 pr-8 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
                  aria-label='Role'
                >
                  <option value='editor'>Editor</option>
                  <option value='viewer'>Viewer</option>
                </select>
                <Button
                  variant='primary'
                  type='submit'
                  isLoading={submitting}
                  disabled={!email}
                  className='!px-4 !py-2 text-sm'
                >
                  Invite
                </Button>
              </div>
            </form>
          )}

          {/* People with access */}
          <div>
            <div className='text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1'>
              People with access
            </div>
            <ul className='divide-y divide-gray-100 dark:divide-gray-800'>
              <li className='flex items-center gap-3 py-2.5'>
                <Avatar
                  src={ownerProfile?.avatar_url}
                  name={ownerName}
                  email={ownerProfile?.email}
                  active={ownerActive}
                />
                <div className='min-w-0 flex-1'>
                  <div className='truncate text-sm font-medium text-gray-900 dark:text-white'>
                    {ownerName}
                    {workflowOwnerId === user?.id && (
                      <span className='ml-1 text-gray-500 dark:text-gray-400'>
                        (You)
                      </span>
                    )}
                  </div>
                  {ownerProfile?.email && (
                    <div className='truncate text-xs text-gray-500 dark:text-gray-400'>
                      {ownerProfile.email}
                    </div>
                  )}
                </div>
                <span className='text-xs text-gray-500 dark:text-gray-400'>
                  Owner
                </span>
              </li>
              {loading && (
                <li className='py-3 text-sm text-gray-500 dark:text-gray-400'>
                  Loading…
                </li>
              )}
              {!loading &&
                collaborators.map((collaborator) =>
                  renderCollaboratorRow(collaborator),
                )}
            </ul>
          </div>

          {/* General access */}
          {isOwner && (
            <div>
              <div className='text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2'>
                General access
              </div>
              <div className='space-y-3'>
                {/* Share via link */}
                <div className='space-y-3'>
                  <div className='flex items-center gap-3'>
                    <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'>
                      <svg
                        className='h-4 w-4'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <path d='M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71' />
                        <path d='M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71' />
                      </svg>
                    </div>
                    <div className='min-w-0 flex-1'>
                      <div className='text-sm font-medium text-gray-900 dark:text-white'>
                        Anyone with the link
                      </div>
                      <div className='text-xs text-gray-500 dark:text-gray-400'>
                        {shareToken
                          ? `Can ${shareLinkRole === 'editor' ? 'edit' : 'view'} this workflow`
                          : 'No one outside has access'}
                      </div>
                    </div>
                    {shareToken && (
                      <select
                        value={shareLinkRole}
                        onChange={(e) =>
                          handleChangeShareLinkRole(
                            e.target.value as 'viewer' | 'editor',
                          )
                        }
                        className='rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 pl-2.5 pr-7 py-1 text-xs focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
                        aria-label='Link role'
                      >
                        <option value='viewer'>Viewer</option>
                        <option value='editor'>Editor</option>
                      </select>
                    )}
                    <Toggle
                      initialValue={!!shareToken}
                      onChange={(value) => {
                        if (value) {
                          handleEnableShareLink();
                        } else {
                          handleDisableShareLink();
                        }
                      }}
                      disabled={shareLinkLoading}
                    />
                  </div>

                  {shareToken && (
                    <div className='flex items-center gap-2 pl-12'>
                      <input
                        type='text'
                        readOnly
                        value={buildShareUrl(shareToken)}
                        onFocus={(e) => e.target.select()}
                        className='flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 outline-none'
                      />
                      <Button
                        variant='outline'
                        type='button'
                        onClick={handleCopyShareLink}
                        className='!px-3 !py-1.5 text-xs whitespace-nowrap'
                      >
                        {linkCopied ? 'Copied' : 'Copy link'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Share with team */}
                <div className='flex items-center gap-3'>
                  <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'>
                    <svg
                      className='h-4 w-4'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M17 21v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2' />
                      <circle cx='9' cy='7' r='4' />
                      <path d='M23 21v-2a4 4 0 00-3-3.87' />
                      <path d='M16 3.13a4 4 0 010 7.75' />
                    </svg>
                  </div>
                  <div className='min-w-0 flex-1'>
                    <div className='text-sm font-medium text-gray-900 dark:text-white'>
                      Your team
                    </div>
                    <div className='text-xs text-gray-500 dark:text-gray-400'>
                      {isTeamsPlan
                        ? shareWithTeam
                          ? 'Team members can view and edit'
                          : 'Team members do not have access'
                        : 'Available on Teams plan'}
                    </div>
                  </div>
                  {isTeamsPlan ? (
                    <Toggle
                      initialValue={shareWithTeam}
                      onChange={handleToggleShareWithTeam}
                    />
                  ) : (
                    <a
                      href='/settings/billing-teams'
                      target='_blank'
                      className='text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 hover:underline'
                    >
                      Upgrade
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-3 dark:border-gray-800'>
          <div className='text-xs text-gray-500 dark:text-gray-400'>
            {activeCollaborators.length > 0 ? (
              <span className='inline-flex items-center gap-1.5'>
                <span className='relative flex h-2 w-2'>
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75' />
                  <span className='relative inline-flex h-2 w-2 rounded-full bg-green-500' />
                </span>
                {activeCollaborators.length}{' '}
                {activeCollaborators.length === 1 ? 'person' : 'people'} active
                now
              </span>
            ) : (
              <span>&nbsp;</span>
            )}
          </div>
          <Button
            variant='outline'
            type='button'
            onClick={onClose}
            className='!px-4 !py-1.5 text-sm'
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
